<template>
  <v-container fluid fill-height class="login-container d-flex justify-center">
    <v-card class="login-card elevation-8">
      <v-card-title class="text-center pa-6">
        <v-img :src="stichhlogo" height="155" />
        <div class="text-h4 font-weight-bold">Partner Login</div>
      </v-card-title>

      <v-card-text class="pa-4">
        <div v-if="!otpSent">
          <v-text-field
            v-model="email"
            label="Email Address"
            prepend-inner-icon="mdi-email"
            type="email"
            variant="outlined"
            :rules="emailRules"
            required
            class="mb-4"
            @keyup.enter="sendOTP"
          />

          <v-btn
            color="primary"
            block
            size="large"
            :loading="loading"
            @click="sendOTP"
            :disabled="!isValidEmail"
            class="mb-3"
          >
            Send OTP
          </v-btn>

          <v-btn
            color="grey"
            variant="text"
            block
            @click="resendOTP"
            :disabled="resendTimer > 0"
            size="small"
          >
            <v-icon left size="small">mdi-refresh</v-icon>
            {{ resendTimer > 0 ? `Resend in ${resendTimer}s` : "Resend OTP" }}
          </v-btn>
        </div>

        <div v-else>
          <div class="text-center mb-6">
            <v-icon size="32" color="success" class="mb-2">
              mdi-email-check
            </v-icon>
            <p class="text-body-2 text-medium-emphasis mb-1">
              Verification code sent to
            </p>
            <p class="text-body-1 font-weight-medium text-primary">
              {{ email }}
            </p>
          </div>

          <div class="text-center mb-6">
            <p class="text-body-2 text-medium-emphasis mb-3">
              Enter the 6-digit code
            </p>
            <v-otp-input
              v-model="otp"
              length="6"
              type="number"
              plain
              class="mb-4"
              @keyup.enter="verifyOTP"
            />
          </div>

          <v-btn
            color="success"
            block
            size="large"
            :loading="loading"
            @click="verifyOTP"
            :disabled="otp.length < 6"
            class="mb-3"
            elevation="2"
          >
            <v-icon left>mdi-login</v-icon>
            <!-- Sign In -->
          </v-btn>

          <div class="d-flex justify-space-between align-center">
            <v-btn
              color="grey"
              variant="text"
              size="small"
              @click="resendOTP"
              :disabled="resendTimer > 0"
            >
              <v-icon left size="small">mdi-refresh</v-icon>
              {{ resendTimer > 0 ? `${resendTimer}s` : "Resend" }}
            </v-btn>

            <v-btn color="grey" variant="text" size="small" @click="goBack">
              <v-icon left size="small">mdi-arrow-left</v-icon>
              Back
            </v-btn>
          </div>
        </div>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import { signOut, signIn, confirmSignIn } from "aws-amplify/auth";
import stichhlogo from "@/assets/LoginPgLogo.png";
export default {
  name: "LoginView",
  data() {
    return {
      userDetails: null,
      stichhlogo,
      email: "",
      otp: "",
      otpSent: false,
      loading: false,
      resendTimer: 0,
      emailRules: [
        (v) => !!v || "Email is required",
        (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) || "Email must be valid",
      ],
      otpRules: [
        (v) => !!v || "OTP is required",
        (v) => v.length === 6 || "Please enter 6-digit OTP",
      ],
    };
  },
  computed: {
    isValidEmail() {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.email);
    },
  },
  async mounted() {
    try {
      await signOut({ global: true });
    } catch (error) {
      console.error("Error signing out:", error);
    }
  },
  methods: {
    startTimer() {
      this.resendTimer = 30;
      const timer = setInterval(() => {
        this.resendTimer--;
        if (this.resendTimer <= 0) {
          clearInterval(timer);
        }
      }, 1000);
    },
    async sendOTP() {
      if (!this.isValidEmail) {
        this.$store.dispatch("setNotification", {
          type: "error",
          message: "Please enter a valid email address",
        });
        return;
      }

      this.loading = true;
      try {
        const logInResult = await signIn({
          username: this.email,
          options: {
            authFlowType: "CUSTOM_WITHOUT_SRP",
          },
        });
        if (logInResult) {
          this.userDetails = logInResult;
          this.otpSent = true;
          this.loading = false;
          this.startTimer();
          this.$store.dispatch("setNotification", {
            type: "success",
            message: "OTP sent Successfully",
          });
        }
      } catch (err) {
        console.log(err);
        if (err.message === "There is already a signed in user.") {
          this.loading = false;
          await signOut({ global: true });
          this.sendOTP();
        } else {
          this.loading = false;
          this.$store.dispatch("setNotification", {
            type: "error",
            message: "Couldn't find your account",
          });
        }
      }
    },

    async verifyOTP() {
      this.loading = true;
      try {
        const user = await confirmSignIn({
          challengeResponse: this.otp,
        });
        this.loading = false;
        if (user.isSignedIn === true) {
          this.$store.dispatch("setUserEmail", this.email);
          this.$router.push("/teams");
        } else {
          this.$store.dispatch("setNotification", {
            type: "error",
            message: "Invalid OTP",
          });
          this.otp = "";
        }
      } catch (error) {
        this.loading = false;
        if (
          error.message ==
          "Invalid version in Lambda response. Version should be 1"
        ) {
          this.$store.dispatch("setNotification", {
            type: "error",
            message: "Invalid OTP",
          });

          this.otp = "";
        } else if (error.message == "Invalid session for the user.") {
          this.$store.dispatch("setNotification", {
            type: "error",
            message: "Multiple Attempts, Please Resend OTP and Login again",
          });

          this.otp = "";
        } else {
          this.$store.dispatch("setNotification", {
            type: "error",
            message: error.message,
          });
          this.otp = "";
        }
      }
    },

    async resendOTP() {
      this.otp = "";
      await this.sendOTP();
    },

    goBack() {
      this.otpSent = false;
      this.otp = "";
      this.resendTimer = 0;
    },

    startResendTimer() {
      this.resendTimer = 30;
      const timer = setInterval(() => {
        this.resendTimer--;
        if (this.resendTimer <= 0) {
          clearInterval(timer);
        }
      }, 1000);
    },
  },
};
</script>

<style scoped>
.login-container {
  height: 100vh;
  background: linear-gradient(135deg, #3f78e0 0%, #da4c77 50%, #fbdef7 100%);
  background-size: 300% 300%;
  animation: rippleFlow 5s ease-in-out infinite;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0;
  padding: 0;
}

@keyframes rippleFlow {
  0% {
    background-position: 0% 0%;
  }
  50% {
    background-position: 100% 100%;
  }
  100% {
    background-position: 0% 0%;
  }
}

.login-card {
  border-radius: 12px;
  max-width: 400px;
  width: 100%;
}

:deep(.v-otp-input) {
  justify-content: center;
}

:deep(.v-container) {
  padding: 0 !important;
  margin: 0 !important;
}

:deep(.v-row) {
  margin: 0 !important;
}

:deep(.v-col) {
  padding: 0 !important;
}
</style>

<style>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

html,
body {
  margin: 0;
  padding: 0;
  height: 100%;
  overflow: hidden;
}

#app {
  height: 100vh;
  margin: 0;
  padding: 0;
}

.v-application {
  margin: 0 !important;
  padding: 0 !important;
  background: transparent !important;
}

.v-application__wrap {
  margin: 0 !important;
  padding: 0 !important;
  min-height: 100vh !important;
}

.v-container {
  margin: 0 !important;
  padding: 0 !important;
}

.v-row {
  margin: 0 !important;
}

.v-col {
  padding: 0 !important;
}
</style>
